import { useEffect } from 'react'
import { FormikProps } from 'formik'
import { IShiftBadgesFormValues } from '@src/features/shift-post-v2/steps/screening-requirements-step/components/screening-section/screening-section.types'
import { IShiftAutoAcceptCertificate } from '@network/models/shift-auto-accept-certificate.model'
import { useDispatch } from 'react-redux'
import { AddBadgesToEditInitialValues } from '@src/features/shift-post-v2/state/shift-post-v2.actions'

type TUseShiftBadgesFormReturnType = {
  handleChange: (id: number) => void
  isCertificateSelected: (id: number) => boolean
}

/**
 * Hook that handles form logic for shift badges
 * @param formik - Formik instance
 * @param autoAcceptCertificates - Array of auto accept certificates
 * @returns {TUseShiftBadgesFormReturnType} Object containing form handlers
 */
export const useShiftBadgesForm = (
  formik: FormikProps<IShiftBadgesFormValues>,
  autoAcceptCertificates: IShiftAutoAcceptCertificate[],
): TUseShiftBadgesFormReturnType => {
  const dispatch = useDispatch()
  const { values, setFieldValue } = formik

  // Initialize badges in form values when certificates are loaded
  useEffect(() => {
    if (autoAcceptCertificates.length > 0 && !values.badges) {
      const badgesWithSelection = autoAcceptCertificates.map(certificate => ({
        ...certificate,
        selected: false,
      }))
      setFieldValue('badges', badgesWithSelection)

      // Set the initial values for the badges
      dispatch(AddBadgesToEditInitialValues(badgesWithSelection))
    }
  }, [autoAcceptCertificates, values.badges, setFieldValue, dispatch])

  useEffect(() => {
    // Update badges selection state based on auto_accept_certificate_ids
    if (values.badges) {
      const updatedBadges = values.badges.map(badge => ({
        ...badge,
        selected:
          values.auto_accept_certificate_ids?.includes(badge.certificate.id) ||
          false,
      }))
      setFieldValue('badges', updatedBadges)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.auto_accept_certificate_ids, setFieldValue])

  const handleChange = (id: number) => {
    const currentIds = values.auto_accept_certificate_ids || []
    const newIds = currentIds.includes(id)
      ? currentIds.filter(i => i !== id)
      : [...currentIds, id]

    setFieldValue('auto_accept_certificate_ids', newIds)
  }

  const isCertificateSelected = (id: number) => {
    return (values.auto_accept_certificate_ids || []).includes(id)
  }

  return {
    handleChange,
    isCertificateSelected,
  }
}
